import { OrderSide, type MarketOrderInfo } from '@/libs/sdk/api';

// Pure formatting/derivation for the activity broadcast line. Kept out of the
// React component so the decimal- and side-sensitive math (the parts that
// actually break) can be unit-tested without a DOM.

type TradeSideAmounts = Pick<MarketOrderInfo, 'side' | 'amount' | 'receiveAmount'>;

// Executed price -> integer cents. dealPrice is per-share collateral in wei of
// `decimal`, so a 0.52e18 price at 18 decimals reads as 52¢.
export const toCents = (dealPrice?: string, decimal = 18): number | null => {
  if (!dealPrice) return null;
  const cents = Number(dealPrice) / 10 ** (decimal - 2);
  return Number.isFinite(cents) ? Math.round(cents) : null;
};

// Token wei -> compact share count (122, 1.2K, 3.5). Small bot trades sit in
// the tens-to-hundreds, so whole numbers read cleanest above 10.
export const fmtShares = (wei: string, decimals: number): string => {
  const v = Number(wei) / 10 ** decimals;
  if (!Number.isFinite(v)) return '0';
  if (v >= 1000) return `${(v / 1000).toFixed(1)}K`;
  if (v >= 10) return String(Math.round(v));
  return v.toFixed(1);
};

// The option-token side of a trade: tokens received on a buy (deposit), tokens
// sold on a sell (withdraw). The collateral side is the other field, so getting
// this backwards would show the money amount where shares belong.
export const sharesWei = (trade: TradeSideAmounts): string =>
  trade.side === OrderSide.deposit ? trade.receiveAmount : trade.amount;
